import { useState } from 'react';
import { Link } from 'react-router-dom';

interface DashboardFilters {
  timeRange: string;
  projects: string[];
  environments: string[];
}

export default function Dashboard() {
  const [filters, setFilters] = useState<DashboardFilters>({
    timeRange: '1h',
    projects: [],
    environments: []
  });

  const [autoRefresh, setAutoRefresh] = useState(false);

  // Mock KPI data
  const kpiData = {
    totalProjects: 8,
    serversUp: 42,
    totalServers: 45,
    componentsHealthy: 127,
    totalComponents: 135,
    criticalAlerts: 3,
    avgLatency: 245
  };

  const timeRanges = [
    { value: '1h', label: '1 Hour' },
    { value: '6h', label: '6 Hours' },
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Overview Dashboard</h1>
            <p className="text-gray-600">Monitor your infrastructure health and performance</p>
          </div>

          <div className="flex items-center space-x-3">
            {/* Time Range Selector */}
            <select
              value={filters.timeRange}
              onChange={(e) => setFilters(prev => ({ ...prev, timeRange: e.target.value }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {timeRanges.map(range => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </select>

            {/* Auto Refresh Toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                autoRefresh
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className={`ri-refresh-line mr-2 ${autoRefresh ? 'animate-spin' : ''}`}></i>
              Auto Refresh
            </button>

            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
              <i className="ri-filter-line mr-2"></i>
              Filters
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Projects */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">Projects</span>
              <i className="ri-folder-line text-blue-600 text-xl"></i>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {kpiData.totalProjects}
            </div>
            <div className="text-sm text-green-600 flex items-center">
              <i className="ri-arrow-up-line mr-1"></i>
              All active
            </div>
          </div>

          {/* Servers */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">Servers</span>
              <i className="ri-server-line text-green-600 text-xl"></i>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {kpiData.serversUp}/{kpiData.totalServers}
            </div>
            <div className="text-sm text-green-600 flex items-center">
              <i className="ri-check-line mr-1"></i>
              {((kpiData.serversUp / kpiData.totalServers) * 100).toFixed(1)}% healthy
            </div>
          </div>

          {/* Components */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">Components</span>
              <i className="ri-stack-line text-purple-600 text-xl"></i>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {kpiData.componentsHealthy}/{kpiData.totalComponents}
            </div>
            <div className="text-sm text-green-600 flex items-center">
              <i className="ri-check-line mr-1"></i>
              {((kpiData.componentsHealthy / kpiData.totalComponents) * 100).toFixed(1)}% healthy
            </div>
          </div>

          {/* Critical Alerts */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">Critical Alerts</span>
              <i className="ri-alert-line text-red-600 text-xl"></i>
            </div>
            <div className="text-3xl font-bold text-red-600 mb-1">
              {kpiData.criticalAlerts}
            </div>
            <div className="text-sm text-gray-600">
              Requires attention
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* System Health Chart */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">System Health Overview</h2>
              <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                <div className="text-center text-gray-500">
                  <i className="ri-line-chart-line text-4xl mb-2"></i>
                  <p>Health metrics chart placeholder</p>
                </div>
              </div>
            </div>

            {/* Incidents Timeline */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Incidents</h2>
              <div className="h-48 flex items-center justify-center bg-gray-50 rounded-lg">
                <div className="text-center text-gray-500">
                  <i className="ri-time-line text-4xl mb-2"></i>
                  <p>Incidents timeline placeholder</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Link
                  to="/app/opsplus/projects"
                  className="flex items-center px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <i className="ri-folder-line text-blue-600 mr-3 text-xl"></i>
                  <span className="font-medium text-gray-900">Manage Projects</span>
                </Link>
                <Link
                  to="/app/opsplus/alerts"
                  className="flex items-center px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <i className="ri-alert-line text-red-600 mr-3 text-xl"></i>
                  <span className="font-medium text-gray-900">View Alerts</span>
                </Link>
                <Link
                  to="/app/opsplus/topology"
                  className="flex items-center px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <i className="ri-node-tree text-purple-600 mr-3 text-xl"></i>
                  <span className="font-medium text-gray-900">View Topology</span>
                </Link>
                <Link
                  to="/app/opsplus/logs"
                  className="flex items-center px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <i className="ri-file-list-line text-green-600 mr-3 text-xl"></i>
                  <span className="font-medium text-gray-900">Browse Logs</span>
                </Link>
              </div>
            </div>

            {/* Top Offenders */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Top CPU Offenders</h3>
              <div className="space-y-3">
                {['web-server-01', 'api-gateway-03', 'db-primary'].map((server, idx) => (
                  <div key={server} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${
                        idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-orange-500' : 'bg-yellow-500'
                      }`}></div>
                      <span className="text-sm text-gray-700">{server}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {(95 - idx * 10)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Recent Activity</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start">
                  <i className="ri-error-warning-line text-red-500 mr-2 mt-0.5"></i>
                  <div>
                    <p className="text-gray-700">Critical alert on server-03</p>
                    <p className="text-gray-500 text-xs">2 minutes ago</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-500 mr-2 mt-0.5"></i>
                  <div>
                    <p className="text-gray-700">Health check passed</p>
                    <p className="text-gray-500 text-xs">5 minutes ago</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-information-line text-blue-500 mr-2 mt-0.5"></i>
                  <div>
                    <p className="text-gray-700">New deployment detected</p>
                    <p className="text-gray-500 text-xs">15 minutes ago</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
